import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import { useOrderHistory } from '../../../../stores/appOrderHistoryStore';
import { useTimeIntervalStore } from '../../../../stores/appTimeIntervalStore';
import { useLoadOrderHistory } from '../../../../utils/dataLoadingUtils';
import { SupplierLogo as SharedSupplierLogo } from '../../../ui/SupplierLogo';
import { eur } from '../../../../components/orderhistory/utils';
import { rangeFromTimeControls, previousRange } from '../../../../utils/reports/shared';
import { cachedReportValue } from '../../../../utils/reports/reportMemoCache';
import { SectionCard, EmptyState, EmptyCard, Kpi, KpiGrid, ReportSelect, ChangeBadge, ReportLoading, ReportTable, ReportColumn, CHART_RED, CHART_BLUE, useChartTheme } from './common';
import {
  computeInflationReport,
  InflationProductRow,
} from '../../../../utils/reports/inflationReport';

/** Format a signed euro amount with an explicit + / − sign. */
const signedEur = (value: number): string => `${value >= 0 ? '+' : '−'}${eur(Math.abs(value))}`;

const SupplierLogo: React.FC<{ row: InflationProductRow }> = ({ row }) => (
  <SharedSupplierLogo platform={row.platform} unknownSupplierName={row.unknownSupplierName} size="small" alt={row.supplierLabel} />
);

const InflationReport: React.FC = () => {
  const { t } = useTranslation();

  // Data is scoped to the selected restaurant by this hook.
  useLoadOrderHistory();

  const orders = useOrderHistory((s) => s.orderHistoryList);
  const isLoadingOrders = useOrderHistory((s) => s.isLoading);

  const view = useTimeIntervalStore((s) => s.view);
  const startDate = useTimeIntervalStore((s) => s.startDate);
  const endDate = useTimeIntervalStore((s) => s.endDate);

  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const report = cachedReportValue(
    'inflation.report',
    [orders, view, startDate, endDate, supplierFilter, categoryFilter],
    () => {
      const range = rangeFromTimeControls(view, startDate, endDate);
      const previous = previousRange(range);
      return computeInflationReport({ orders, range, previous, supplierFilter, categoryFilter });
    }
  );

  // While orders are still loading and empty, show a spinner instead of flashing
  // the empty / all-view explainers.
  if (isLoadingOrders && orders.length === 0) {
    return <ReportLoading />;
  }

  if (report.comparisonHidden) {
    return <EmptyCard message={t('reports.inflation.allViewHint')} />;
  }

  if (!report.hasData) {
    return <EmptyCard message={t('reports.inflation.empty')} />;
  }

  return (
    <div className="mt-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <ReportSelect value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)}>
          <option value="all">{t('reports.inflation.filter.allSuppliers')}</option>
          {report.supplierOptions.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label || t('reports.inflation.unknownSupplier')}
            </option>
          ))}
        </ReportSelect>
        <ReportSelect value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="all">{t('reports.inflation.filter.allCategories')}</option>
          {report.categoryOptions.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </ReportSelect>
      </div>

      {/* Totals banner */}
      <KpiGrid className="mb-2">
        <Kpi
          label={t('reports.inflation.kpi.spendChange')}
          value={signedEur(report.totalSpendChange)}
          tone={report.totalSpendChange > 0.005 ? 'up' : report.totalSpendChange < -0.005 ? 'down' : 'neutral'}
        />
        <Kpi
          label={t('reports.inflation.kpi.priceImpact')}
          value={signedEur(report.priceImpactTotal)}
          tone={report.priceImpactTotal > 0.005 ? 'up' : report.priceImpactTotal < -0.005 ? 'down' : 'neutral'}
        />
        <Kpi
          label={t('reports.inflation.kpi.volumeImpact')}
          value={signedEur(report.volumeImpactTotal)}
          tone={report.volumeImpactTotal > 0.005 ? 'up' : report.volumeImpactTotal < -0.005 ? 'down' : 'neutral'}
        />
        <Kpi
          label={t('reports.inflation.kpi.newProducts')}
          value={`${signedEur(report.newProductsSpend)} · ${report.newProductsCount}`}
        />
        <Kpi
          label={t('reports.inflation.kpi.droppedProducts')}
          value={`${signedEur(-report.droppedProductsSpend)} · ${report.droppedProductsCount}`}
        />
      </KpiGrid>
      <p className="text-xs text-muted dark:text-muted-dark mb-6">
        {t('reports.inflation.reconcile', {
          total: signedEur(report.totalSpendChange),
          price: signedEur(report.priceImpactTotal),
          volume: signedEur(report.volumeImpactTotal),
          added: signedEur(report.newProductsSpend),
          dropped: signedEur(-report.droppedProductsSpend),
        })}
      </p>

      {/* Price vs volume decomposition chart */}
      {report.topRisers.length > 0 && (
        <SectionCard title={t('reports.inflation.chart.title')} subtitle={t('reports.inflation.chart.subtitle')}>
          <ImpactChart rows={report.topRisers} t={t} />
        </SectionCard>
      )}

      {/* Top risers */}
      <SectionCard
        title={t('reports.inflation.risers.title')}
        subtitle={t('reports.inflation.risers.subtitle')}
      >
        {report.topRisers.length === 0 ? (
          <EmptyState message={t('reports.inflation.risers.empty')} />
        ) : (
          <ImpactTable rows={report.topRisers} t={t} />
        )}
      </SectionCard>

      {/* Top fallers */}
      <SectionCard
        title={t('reports.inflation.fallers.title')}
        subtitle={t('reports.inflation.fallers.subtitle')}
      >
        {report.topFallers.length === 0 ? (
          <EmptyState message={t('reports.inflation.fallers.empty')} />
        ) : (
          <ImpactTable rows={report.topFallers} t={t} />
        )}
      </SectionCard>

      <p className="text-xs text-muted dark:text-muted-dark mb-2">
        {t('reports.inflation.decompositionFootnote')}
      </p>
      {report.flaggedCount > 0 && (
        <p className="text-xs text-muted dark:text-muted-dark inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 text-amber-500 dark:text-darkOrangeColor-dark" />
          {t('reports.inflation.flagFootnote')}
        </p>
      )}
    </div>
  );
};

// --- impact chart ---------------------------------------------------------

/** Grouped horizontal bars: per product, the € from price changes vs the € from
 * volume changes. Makes "is cost up because of prices or because we bought more?"
 * readable at a glance. */
const ImpactChart: React.FC<{ rows: InflationProductRow[]; t: TFunction }> = ({ rows, t }) => {
  const theme = useChartTheme();
  const data = rows.map((r) => ({
    label: r.name.length > 22 ? `${r.name.slice(0, 21)}…` : r.name,
    price: Math.round(r.priceImpactEur * 100) / 100,
    volume: Math.round(r.volumeImpactEur * 100) / 100,
  }));
  return (
    <div style={{ width: '100%', height: Math.max(180, data.length * 40 + 40) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
          <XAxis type="number" tickFormatter={(v: number) => eur(v)} tick={{ fill: theme.axisTick, fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="label" width={150} tick={{ fill: theme.axisTick, fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip
            formatter={(v: number) => eur(v)}
            cursor={{ fill: theme.cursorFill }}
            contentStyle={{ backgroundColor: theme.tooltipBg, borderColor: theme.tooltipBorder, color: theme.tooltipText }}
          />
          <Legend />
          <ReferenceLine x={0} stroke={theme.referenceLine} />
          <Bar dataKey="price" name={t('reports.inflation.kpi.priceImpact')} fill={CHART_RED} radius={[0, 3, 3, 0]} />
          <Bar dataKey="volume" name={t('reports.inflation.kpi.volumeImpact')} fill={CHART_BLUE} radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// --- impact table ---------------------------------------------------------

const ImpactTable: React.FC<{ rows: InflationProductRow[]; t: TFunction }> = ({ rows, t }) => {
  const columns: ReportColumn<InflationProductRow>[] = [
    {
      key: 'product',
      header: t('reports.inflation.table.product'),
      render: (r) => (
        <div className="flex items-center gap-2">
          <SupplierLogo row={r} />
          <div>
            <span className="font-medium inline-flex items-center gap-1">
              {r.name}
              {r.unitFlag && (
                <AlertTriangle
                  className="w-3 h-3 text-amber-500 dark:text-darkOrangeColor-dark"
                  aria-label={t(
                    r.unitFlagReason === 'packageSizeChanged'
                      ? 'reports.inflation.flag.packageSizeChanged'
                      : 'reports.inflation.flag.missingQuantityInfo'
                  )}
                />
              )}
              {r.notDecomposable && (
                <AlertTriangle className="w-3 h-3 text-amber-500 dark:text-darkOrangeColor-dark" aria-label={t('reports.inflation.flag.notDecomposable')} />
              )}
            </span>
            {r.categoryDisplay && <span className="block text-xs text-muted dark:text-muted-dark">{r.categoryDisplay}</span>}
          </div>
        </div>
      ),
    },
    { key: 'spendPrev', header: t('reports.inflation.table.spendPrev'), align: 'right', render: (r) => <span className="text-muted dark:text-muted-dark">{eur(r.spendPrev)}</span> },
    { key: 'spendNow', header: t('reports.inflation.table.spendNow'), align: 'right', render: (r) => eur(r.spendNow) },
    { key: 'priceChange', header: t('reports.inflation.table.priceChange'), align: 'right', render: (r) => <ChangeBadge value={r.priceChangePercent} goodWhenUp={false} /> },
    { key: 'priceImpact', header: t('reports.inflation.table.priceImpact'), align: 'right', render: (r) => <span className="font-medium">{signedEur(r.priceImpactEur)}</span> },
    { key: 'volumeImpact', header: t('reports.inflation.table.volumeImpact'), align: 'right', render: (r) => <span className="text-muted dark:text-muted-dark">{signedEur(r.volumeImpactEur)}</span> },
    { key: 'spendDelta', header: t('reports.inflation.table.spendDelta'), align: 'right', render: (r) => <span className="font-medium">{signedEur(r.spendDeltaEur)}</span> },
  ];
  return <ReportTable columns={columns} rows={rows} rowKey={(r) => r.key} />;
};

export default InflationReport;
